/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Box,
    Stack
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'

import {defineMessages, useIntl, FormattedMessage} from 'react-intl'
import {Component, PageContext} from '@salesforce/commerce-sdk-react/components'
import {PDCShippingPDPDetailsLayout, PDCStyleGuideCarousel} from '../../../page-designer/layouts'
import {PDCShippingPDPDetailsPD, PDCStyleGuideTile} from '../../../page-designer/assets'
import {usePage} from '@salesforce/commerce-sdk-react'
import {HideOnDesktop} from '@salesforce/retail-react-app/app/components/responsive'
import {pageUrlBuilder} from '../../../utils/url-utils'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'

import {
    STYLEGUIDE_MEN_PAGE_ID,
    STYLEGUIDE_WOMEN_PAGE_ID,
    CODE_MARCHE_MEN,
    CODE_MARCHE_WOMEN,
    CODE_FORM_MDB
} from '../../../constants'
import useSiteType from '@app/hooks/use-site-type'

//utils
import {generateProductDetailsHTML, isAGECProduct} from '../../../utils/product-utils'
import {getEnvStaleTime, getPageDesignerRegion} from '../../../utils/utils'

//css
import {productDetailsStyles} from '../../../theme/components/project/product-details'
import {productCareStyles} from '../../../theme/components/project/product-care'
import ProductAGEC from '../../../components/product-view/product-agec'

// hooks
import {usePageParameters} from '@app/hooks/use-page-parameters'

const informationAccordionMessages = defineMessages({
    productDetail: {
        id: 'product_detail.accordion.button.product_detail',
        defaultMessage: 'Product Detail'
    },
    traceability: {
        id: 'product_detail.accordion.button.traceability',
        defaultMessage: 'Traceability'
    },
    care: {
        id: 'product_detail.accordion.button.care',
        defaultMessage: 'Care'
    },
    reviews: {
        id: 'product_detail.accordion.button.reviews',
        defaultMessage: 'Reviews'
    },
    questions: {
        id: 'product_detail.accordion.button.questions',
        defaultMessage: 'Questions'
    },
    shipping: {
        id: 'product_detail.accordion.button.shipping',
        defaultMessage: 'Shipping'
    },
    comingSoon: {
        id: 'product_detail.accordion.message.coming_soon',
        defaultMessage: 'Coming Soon'
    }
})

/**
 * AccordionSection is a reusable component for rendering a section within an accordion.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.titleMessage - The message object for the title, containing `id` and `defaultMessage`.
 * @param {string} props.titleMessage.id - The ID of the message for internationalization.
 * @param {string} props.titleMessage.defaultMessage - The default message to display.
 * @param {React.ReactNode} props.content - The content to display within the accordion panel.
 * @returns {JSX.Element} The rendered accordion section.
 */
const AccordionSection = ({titleMessage, content}) => {
    const {formatMessage} = useIntl()

    return (
        <AccordionItem>
            <h2>
                <AccordionButton height="64px" pl="10px">
                    <Box flex="1" textAlign="left" fontWeight="bold" fontSize="lg">
                        {formatMessage(titleMessage)}
                    </Box>
                    <AccordionIcon />
                </AccordionButton>
            </h2>
            <AccordionPanel mb={6} mt={4}>
                {content}
            </AccordionPanel>
        </AccordionItem>
    )
}

AccordionSection.propTypes = {
    titleMessage: PropTypes.shape({
        id: PropTypes.string.isRequired,
        defaultMessage: PropTypes.oneOfType([PropTypes.array, PropTypes.string])
    }).isRequired,
    content: PropTypes.node.isRequired
}

/**
 * InformationAccordion Component
 *
 * This component displays detailed information about a product in an accordion format.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.product - The product object containing details to be displayed.
 * @returns {JSX.Element} The rendered InformationAccordion component.
 */
const InformationAccordion = ({productDetails, shortDescription, longDescription, product}) => {
    const staleTime = getEnvStaleTime()
    const {formatMessage} = useIntl()
    const PAGEDESIGNER_TO_COMPONENT = {
        'commerce_assets.pdcShippingPDP': PDCShippingPDPDetailsPD,
        'commerce_assets.pdcStyleGuideTile': PDCStyleGuideTile,
        'commerce_layouts.pdcShippingPDPLayout': PDCShippingPDPDetailsLayout,
        'commerce_layouts.pdcStyleGuideCarousel': PDCStyleGuideCarousel
    }
    const [contextValue] = useState({components: PAGEDESIGNER_TO_COMPONENT})
    const {isOutlet} = useSiteType()
    const parameters = usePageParameters('pdp-data' + (isOutlet ? '-outlet' : ''), {
        includeCountry: true
    })
    const {data: shippingPDP} = usePage({parameters})
    const shippingPDPComponent = getPageDesignerRegion(shippingPDP, 'main').components?.find(
        (comp) => comp?.typeId === 'commerce_layouts.pdcShippingPDPLayout'
    )

    // Get style guide PD component
    const getStyleGuideItem = (marketCode, menItem, womenItem) => {
        switch (marketCode) {
            case CODE_MARCHE_MEN:
                return menItem
            case CODE_MARCHE_WOMEN:
                return womenItem
            default:
                return ''
        }
    }
    const {data: styleGuideMenPage} = usePage(
        {parameters: {pageId: STYLEGUIDE_MEN_PAGE_ID}},
        {staleTime: staleTime}
    )
    const {data: styleGuideWomenPage} = usePage(
        {parameters: {pageId: STYLEGUIDE_WOMEN_PAGE_ID}},
        {staleTime: staleTime}
    )
    const styleGuidePage =
        product?.c_orliweb_code_form === CODE_FORM_MDB
            ? getStyleGuideItem(
                  product?.c_orliweb_stat03_code_marche,
                  styleGuideMenPage,
                  styleGuideWomenPage
              )
            : null
    const styleGuidePageId =
        product?.c_orliweb_code_form === CODE_FORM_MDB
            ? getStyleGuideItem(
                  product?.c_orliweb_stat03_code_marche,
                  STYLEGUIDE_MEN_PAGE_ID,
                  STYLEGUIDE_WOMEN_PAGE_ID
              )
            : ''
    const styleGuideComponent = styleGuidePage?.regions
        ?.find((region) => region.id === 'main')
        .components?.find((comp) => comp?.typeId === 'commerce_layouts.pdcStyleGuideCarousel')
    if (styleGuideComponent) {
        styleGuideComponent.isPDP = true
        styleGuideComponent.selectedModelId = product?.c_orliweb_product_model
    }
    const isAGECData = isAGECProduct(product)

    return (
        <HideOnDesktop>
            <Stack direction="row" spacing={[0, 0, 0, 16]}>
                <Accordion
                    allowMultiple
                    maxWidth={{base: '100%', md: '100%', lg: ''}}
                    flex={[1, 1, 1, 5]}
                >
                    <AccordionSection
                        titleMessage={informationAccordionMessages.productDetail}
                        content={
                            <Box pb={20}>
                                <Box
                                    p={'10px'}
                                    css={productDetailsStyles}
                                    dangerouslySetInnerHTML={{
                                        __html: generateProductDetailsHTML({
                                            productDetails,
                                            shortDescription,
                                            longDescription
                                        })
                                    }}
                                ></Box>
                                {styleGuideComponent && (
                                    <>
                                        <PageContext.Provider value={contextValue}>
                                            <Component component={styleGuideComponent} />
                                        </PageContext.Provider>
                                        <Link
                                            to={pageUrlBuilder(styleGuidePageId)}
                                            ml={10}
                                            mt={20}
                                            display={'inline-block'}
                                        >
                                            <FormattedMessage
                                                defaultMessage="View all styles"
                                                id="product_details_carousel.link.view_all_styles"
                                            />
                                        </Link>
                                    </>
                                )}
                            </Box>
                        }
                    />
                    {isAGECData && (
                        <AccordionSection
                            titleMessage={informationAccordionMessages.traceability}
                            content={
                                <ProductAGEC
                                    isAgecProduct={isAGECData}
                                    madeIn={product.c_orliweb_code_pays_made_in}
                                    origineMatiere={product.c_orliweb_code_pays_origine_matiere}
                                    transformation={product.c_orliweb_code_pays_transformation}
                                    pourcentageMatiereRecyclee={
                                        product.c_orliweb_pourcentage_matiere_recyclee
                                    }
                                    contientMicroplastique={
                                        product.c_orliweb_contient_microplastique
                                    }
                                    contientSubstanceDangereuse={
                                        product.c_orliweb_contient_substance_dangereuse
                                    }
                                    agecPdfLocation={product.c_AGECPDFLocation}
                                />
                            }
                        />
                    )}
                    <AccordionSection
                        titleMessage={informationAccordionMessages.care}
                        content={
                            <Box>
                                {product?.['c_careInstructions'] && (
                                    <Box
                                        pl={'10px'}
                                        width={'394px'}
                                        fontSize={'md'}
                                        css={productCareStyles}
                                        dangerouslySetInnerHTML={{
                                            __html: product?.['c_careInstructions']
                                        }}
                                    ></Box>
                                )}
                            </Box>
                        }
                    />
                    {/* TODO this part should be implemented with https://vilebrequin.atlassian.net/browse/VRAM-787
                    <AccordionSection
                        titleMessage={informationAccordionMessages.reviews}
                        content={formatMessage(informationAccordionMessages.comingSoon)}
                    /> */}

                    {/* 
                        NOTE:
                        We do NOT render the Shipping section on SSR.
                        Page Designer shipping components depend on 
                        customer group. Rendering them server-side would
                        produce content that may not match the client (hydration mismatch) 
                        or may expose irrelevant/incorrect PD content.
                    */}
                    {!isServer && (
                        <AccordionSection
                            titleMessage={informationAccordionMessages.shipping}
                            content={
                                <Box pl={'10px'}>
                                    <PageContext.Provider value={contextValue}>
                                        {shippingPDPComponent ? (
                                            <Component component={shippingPDPComponent} />
                                        ) : null}
                                    </PageContext.Provider>
                                </Box>
                            }
                        />
                    )}
                </Accordion>
                <Box display={['none', 'none', 'none', 'block']} flex={4}></Box>
            </Stack>
        </HideOnDesktop>
    )
}

InformationAccordion.propTypes = {
    productDetails: PropTypes.string,
    shortDescription: PropTypes.string,
    longDescription: PropTypes.string,
    product: PropTypes.object
}

export default InformationAccordion
